import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import {
  LayoutDashboard,
  Users,
  User,
  Car,
  CalendarCheck,
  FileText,
  Receipt,
  BarChart3,
  FileCode,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  Wrench,
  Wallet,
  Shield
} from 'lucide-react';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard',     path: '/dashboard',    module: 'dashboard' },
  { icon: Users,           label: 'Clientes',      path: '/clientes',     module: 'clientes' },
  { icon: Car,             label: 'Veículos',      path: '/veiculos',     module: 'veiculos' },
  { icon: Wrench,          label: 'Manutenções',   path: '/manutencoes',  module: 'manutencoes' },
  { icon: CalendarCheck,   label: 'Reservas',      path: '/reservas',     module: 'reservas' },
  { icon: FileText,        label: 'Contratos',     path: '/contratos',    module: 'contratos' },
  { icon: Receipt,         label: 'Faturamento',   path: '/faturamento',  module: 'faturamento' },
  { icon: Bell,            label: 'Notificações',  path: '/notificacoes', module: 'faturamento' },
  { icon: Wallet,          label: 'Financeiro',    path: '/financeiro',   module: 'financeiro' },
  { icon: BarChart3,       label: 'Relatórios',    path: '/relatorios',   module: 'relatorios' },
  { icon: FileCode,        label: 'Documentos',    path: '/documentos',   module: 'documentos' },
  { icon: Settings,        label: 'Configurações', path: '/configuracoes',module: 'configuracoes' },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, API } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [naoNotificadas, setNaoNotificadas] = useState(0);

  const fetchNotifCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/notificacoes/stats`, { withCredentials: true });
      setNaoNotificadas(res.data.nao_notificadas || 0);
    } catch {
      // silently fail — não interrompe o layout
    }
  }, [API]);

  // Busca ao montar e repete a cada 5 minutos
  useEffect(() => {
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifCount]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Função para verificar se o usuário tem permissão para acessar um módulo
  const hasPermission = (module) => {
    if (!user) return false;
    
    // Admin tem acesso a tudo
    if (user.role === 'admin') return true;
    
    // Se não tem permissões definidas, negar acesso
    if (!user.permissions) return false;
    
    // Verificar permissão de visualização do módulo
    const modulePerms = user.permissions[module];
    if (!modulePerms) return false;
    
    return modulePerms.view === true;
  };

  // Filtrar menu items baseado nas permissões do usuário
  const filteredMenuItems = menuItems.filter(item => hasPermission(item.module));

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="main-layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900 font-[Manrope]">GOLOC</span>
          </Link>
          <button 
            className="lg:hidden p-1 hover:bg-gray-100 rounded"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.slice(1)}`}
                className={`sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                  isActive 
                    ? 'bg-blue-50 text-blue-600 font-semibold' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.path === '/notificacoes' && naoNotificadas > 0 && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center leading-none"
                    data-testid="sidebar-notif-badge"
                  >
                    {naoNotificadas > 99 ? '99+' : naoNotificadas}
                  </span>
                )}
              </Link>
            );
          })}
          
          {/* Admin Link - Only for admins */}
          {user?.role === 'admin' && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className={`sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                location.pathname === '/admin'
                  ? 'bg-red-50 text-red-600 font-semibold' 
                  : 'text-red-600 hover:bg-red-50'
              }`}
              onClick={() => setSidebarOpen(false)}
            >
              <Shield className="w-5 h-5" />
              Administração
            </Link>
          )}
        </nav>

        {/* User section at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 w-full text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            Sair
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 lg:px-6">
          <div className="h-full flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900 font-[Manrope]">
                {menuItems.find(item => location.pathname.startsWith(item.path))?.label || 'GOLOC'}
              </h1>
            </div>

            <div className="flex items-center gap-4">
              <button
                className="p-2 hover:bg-gray-100 rounded-lg relative"
                data-testid="notifications-button"
                onClick={() => navigate('/notificacoes')}
                title={naoNotificadas > 0 ? `${naoNotificadas} faturas vencidas sem notificação` : 'Notificações de cobrança'}
              >
                <Bell className={`w-5 h-5 ${naoNotificadas > 0 ? 'text-red-500' : 'text-gray-500'}`} />
                {naoNotificadas > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-sm"
                    data-testid="header-notif-badge"
                  >
                    {naoNotificadas > 99 ? '99+' : naoNotificadas}
                  </span>
                )}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded-lg" data-testid="user-menu-button">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} />
                      <AvatarFallback className="bg-blue-600 text-white text-sm">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-sm font-medium text-gray-700">
                      {user?.name?.split(' ')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/perfil')}>
                    <User className="w-4 h-4 mr-2" />
                    Meu Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/configuracoes')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
